import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { jsonFetch } from '@/lib/api/jsonFetch'
import type {
  ApproveImpactsBody,
  BulkStatusResponse,
  ApproveImpactsResponse,
  CollisionStatus,
  FlagImpactResponse,
  FlagListResponse,
  FlagStatus,
  ManualEntryBody,
  PoCollision,
  UpcomingChangesResponse,
  VendorPriceChangeFlag,
} from './types'

const ROOT = '/api/vendor-price-changes'

export interface FlagListParams {
  /** A single status, or a comma-joined list (e.g. `'open,reviewing'`
   *  for "the active queue") — the backend splits on `,`. */
  status?: FlagStatus | string
  direction?: 'increase' | 'decrease'
  sort?: 'detected_at' | 'pct_change' | 'abs_change'
}

function toQuery(params: FlagListParams): string {
  const parts: string[] = []
  if (params.status) parts.push(`status=${encodeURIComponent(params.status)}`)
  if (params.direction) parts.push(`direction=${encodeURIComponent(params.direction)}`)
  if (params.sort) parts.push(`sort=${encodeURIComponent(params.sort)}`)
  return parts.length ? `?${parts.join('&')}` : ''
}

export function useVendorPriceChangeFlags(params: FlagListParams) {
  return useQuery({
    queryKey: ['vendor-price-changes', 'flags', params],
    queryFn: () => jsonFetch<FlagListResponse>(`${ROOT}/flags${toQuery(params)}`),
    staleTime: 15_000,
  })
}

export function useVendorPriceChangeFlag(flagId: number | null) {
  return useQuery({
    queryKey: ['vendor-price-changes', 'flag', flagId],
    queryFn: () => jsonFetch<VendorPriceChangeFlag>(`${ROOT}/flags/${flagId}`),
    enabled: flagId !== null,
    staleTime: 15_000,
  })
}

export function useVendorPriceChangeImpact(flagId: number | null) {
  return useQuery({
    queryKey: ['vendor-price-changes', 'impact', flagId],
    queryFn: () => jsonFetch<FlagImpactResponse>(`${ROOT}/flags/${flagId}/impact`),
    enabled: flagId !== null,
    staleTime: 5_000,
  })
}

export function useManualPriceEntry() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: ManualEntryBody) =>
      jsonFetch<{ success: boolean; flag_id?: number }>(`${ROOT}/manual-entry`, {
        method: 'POST',
        body,
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['vendor-price-changes', 'flags'] })
    },
  })
}

export interface ApproveImpactsVars extends ApproveImpactsBody {
  flagId: number
}

export function useApproveImpacts() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (vars: ApproveImpactsVars) =>
      jsonFetch<ApproveImpactsResponse>(`${ROOT}/flags/${vars.flagId}/approve`, {
        method: 'POST',
        body: { impact_ids: vars.impact_ids, price_overrides: vars.price_overrides },
      }),
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({ queryKey: ['vendor-price-changes', 'impact', vars.flagId] })
      void qc.invalidateQueries({ queryKey: ['vendor-price-changes', 'flags'] })
    },
  })
}

export interface SetFlagStatusVars {
  flagId: number
  status: FlagStatus
  notes?: string
}

export function useSetFlagStatus() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (vars: SetFlagStatusVars) =>
      jsonFetch<VendorPriceChangeFlag>(`${ROOT}/flags/${vars.flagId}/status`, {
        method: 'POST',
        body: { status: vars.status, notes: vars.notes },
      }),
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({ queryKey: ['vendor-price-changes', 'flag', vars.flagId] })
      void qc.invalidateQueries({ queryKey: ['vendor-price-changes', 'flags'] })
    },
  })
}

export interface SetCollisionStatusVars {
  flagId: number
  collisionId: number
  status: CollisionStatus
  notes?: string
}

export function useSetCollisionStatus() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (vars: SetCollisionStatusVars) =>
      jsonFetch<PoCollision>(`${ROOT}/flags/${vars.flagId}/po-collisions/${vars.collisionId}/status`, {
        method: 'POST',
        body: { status: vars.status, notes: vars.notes },
      }),
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({ queryKey: ['vendor-price-changes', 'impact', vars.flagId] })
      void qc.invalidateQueries({ queryKey: ['vendor-price-changes', 'flags'] })
    },
  })
}

export interface BulkSetFlagStatusVars {
  flagIds: number[]
  status: FlagStatus
  notes?: string
}

export function useBulkSetFlagStatus() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (vars: BulkSetFlagStatusVars) =>
      jsonFetch<BulkStatusResponse>(`${ROOT}/flags/status/bulk`, {
        method: 'POST',
        body: { flag_ids: vars.flagIds, status: vars.status, notes: vars.notes },
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['vendor-price-changes', 'flags'] })
    },
  })
}

const INTAKE_ROOT = '/api/vendor-pricelist-intake'

/** Scheduled vendor price lines across all vendors, soonest first. */
export function useUpcomingPriceChanges() {
  return useQuery({
    queryKey: ['vendor-price-changes', 'upcoming'],
    queryFn: () => jsonFetch<UpcomingChangesResponse>(`${INTAKE_ROOT}/scheduled`),
    staleTime: 60_000,
    refetchInterval: 5 * 60_000,
  })
}

export function useUnscheduleUpcoming() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (vars: { intakeId: string; lineIds: number[] }) =>
      jsonFetch<{ unscheduled: number[] }>(`${INTAKE_ROOT}/intake/${vars.intakeId}/lines/unschedule`, {
        method: 'POST',
        body: { line_ids: vars.lineIds },
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['vendor-price-changes', 'upcoming'] })
      void qc.invalidateQueries({ queryKey: ['vendor-pricelists'] })
    },
  })
}
